import { useEffect, useState } from 'react';
import { Button, Tag } from 'antd';
import { ReactComponent as MockIcon } from '@app/assets/icons/mock.svg';
import { IMockTestHis } from '@app/api/mockTest.api';
import { Table } from 'components/common/Table/Table';
import { MOCK_TEST_USER_STATUS } from '@app/constants/mockTest';
import { useTranslation } from 'react-i18next';

interface Column {
  title: string;
  dataIndex: string;
  key?: string;
  render?: (text: any, record: any, index: number) => JSX.Element;
  width?: string;
}

export interface MockTestProps {
  mockTestHis: IMockTestHis[];
  onContinue: () => void;
  onResultFinished: (mockTestUserId: string) => void;
  onResultDone: (mockTestUserId: string) => void;
  onDelete: (mockTestUserId: string) => void;
}

export const ExamMockTestTable: React.FC<MockTestProps> = ({
  mockTestHis,
  onContinue,
  onResultDone,
  onDelete,
  onResultFinished,
}) => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<Column[]>([]);
  const [data, setData] = useState<IMockTestHis[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setData(mockTestHis);
  }, [mockTestHis]);

  useEffect(() => {
    initTable();
  }, [t]);

  const initTable = () => {
    setColumns([
      {
        title: t('page.results.mockName'),
        dataIndex: 'name',
        render: (mock_name: string) => (
          <div className="flex gap-3 items-center">
            <MockIcon />
            <span className="font-bold text-base uppercase">{mock_name}</span>
          </div>
        ),
      },
      {
        title: t('page.results.status'),
        dataIndex: 'status',
        render: (status: string) => {
          const statusColors: Record<string, string> = {
            DONE: 'success',
            IN_PROGRESS: 'processing',
            CANCELLED: 'error',
            FINISHED: 'warning',
          };

          return (
            <Tag color={statusColors[status] || 'default'} key={status}>
              {t(`page.results.${status}`)}
            </Tag>
          );
        },
      },
      {
        title: t('page.results.score'),
        dataIndex: 'overallscore',
        render: (overallscore: number, record: IMockTestHis) => {
          if (record.status === MOCK_TEST_USER_STATUS.DONE) {
            return <span>{`${overallscore}`}</span>;
          }
          return <span>--</span>;
        },
      },
      {
        title: t('page.results.createdDate'),
        dataIndex: 'startdate',
        render: (startdate: string) => {
          const date = new Intl.DateTimeFormat('en-GB').format(new Date(startdate));
          return <span>{`${date}`}</span>;
        },
      },
      {
        title: t('page.results.actions'),
        dataIndex: 'status',
        render: (status: string, record: IMockTestHis) => {
          const ActionComponent = actionButtons[status as keyof typeof actionButtons];
          return <div className="flex gap-2">{ActionComponent?.(record) || null}</div>;
        },
      },
    ]);
  };

  const actionButtons = {
    [MOCK_TEST_USER_STATUS.DONE]: (record: IMockTestHis) => (
      <>
        <Button size="small" onClick={() => onResultDone(record.id)}>
          {t('page.mockTest.button.result')}
        </Button>
        <Button size="small" onClick={() => onDelete(record.id)}>
          {t('page.mockTest.button.delete')}
        </Button>
      </>
    ),
    [MOCK_TEST_USER_STATUS.IN_PROGRESS]: (record: IMockTestHis) => (
      <>
        <Button size="small" onClick={onContinue}>
          {t('page.mockTest.button.continue')}
        </Button>
        <Button size="small" onClick={() => onDelete(record.id)}>
          {t('page.mockTest.button.delete')}
        </Button>
      </>
    ),
    [MOCK_TEST_USER_STATUS.FINISHED]: (record: IMockTestHis) => (
      <>
        <Button size="small" onClick={() => onResultFinished(record.id)}>
          {t('page.mockTest.button.view')}
        </Button>
        <Button size="small" onClick={() => onDelete(record.id)}>
          {t('page.mockTest.button.delete')}
        </Button>
      </>
    ),
    [MOCK_TEST_USER_STATUS.CANCELLED]: (record: IMockTestHis) => (
      <Button size="small" onClick={() => onDelete(record.id)}>
        {t('page.mockTest.button.delete')}
      </Button>
    ),
  };

  return (
    <Table
      columns={columns}
      dataSource={data}
      loading={loading}
      scroll={{ x: 800 }}
      bordered
      pagination={false}
      rowKey="id"
    />
  );
};
